import React, { useState } from 'react';
import { Download, Copy } from 'lucide-react';
import { Document, Packer, Paragraph, TextRun, AlignmentType, HeadingLevel } from 'docx';
import { copyWithToast } from '../../utils/clipboard';

interface DraftDetailedDescriptionDisplayProps {
  result: any;
  theme?: 'light' | 'dark';
  addToResources?: (resource: any) => void;
  showToast?: (message: string) => void;
  contextData?: any;
  searchTerm?: string;
  matchesWildcard?: (text: string, pattern: string) => boolean;
  showToggle?: boolean;
  onAddToInput?: (text: string) => void;
  onCopyResult?: () => void;
  showHeader?: boolean;
}

const DraftDetailedDescriptionDisplay: React.FC<DraftDetailedDescriptionDisplayProps> = ({
  result,
  theme = 'light',
  addToResources,
  showToast,
  contextData,
  searchTerm = '',
  matchesWildcard,
  showToggle = false,
  onAddToInput,
  onCopyResult,
  showHeader = true
}) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const isDarkMode = theme === 'dark';

  // Helper function to copy text to clipboard
  const copyToClipboard = (text: string) => copyWithToast(text, showToast);

  // Handle both direct result and nested output structure
  const detailedDescription = result?.detailed_description || result?.output?.detailed_description || result;

  // Paragraph mode detection
  const isParagraphMode = detailedDescription && typeof detailedDescription === 'object' && (
    detailedDescription.output_format === 'paragraphs' || !!detailedDescription.detailed_description_text
  );

  // Self-detection: check if this looks like a draft detailed description
  const isDraftDetailedDescription = isParagraphMode || (
    detailedDescription &&
    typeof detailedDescription === 'object' &&
    Array.isArray(detailedDescription.sections) &&
    detailedDescription.sections.length > 0 &&
    detailedDescription.sections.some((section: any) =>
      section.heading && Array.isArray(section.paragraphs)
    )
  );

  if (!isDraftDetailedDescription) {
    return null; // Not a draft detailed description, let other components handle it
  }

  if (!isParagraphMode && (!detailedDescription || !detailedDescription.sections)) {
    return null;
  }

  // ---- Paragraph mode render ----
  if (isParagraphMode) {
    const detailedDescriptionText: string = detailedDescription.detailed_description_text || '';

    const exportParagraphsToDocx = async () => {
      try {
        const paras = detailedDescriptionText.split('\n\n').filter((p: string) => p.trim());
        const docChildren: any[] = paras.map((para: string) => {
          const trimmed = para.trim();
          const isHeading = trimmed === trimmed.toUpperCase() && trimmed.length < 80 && /[A-Z]/.test(trimmed);
          if (isHeading) {
            return new Paragraph({ text: trimmed, heading: HeadingLevel.HEADING_2, spacing: { before: 300, after: 200 } });
          }
          return new Paragraph({ text: trimmed, spacing: { after: 200, line: 360 } });
        });
        const doc = new Document({ sections: [{ properties: {}, children: docChildren }] });
        const blob = await Packer.toBlob(doc);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'draft-detailed-description.docx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        showToast?.('Draft Detailed Description exported to Word document');
      } catch (error) {
        console.error('Error exporting paragraphs to DOCX:', error);
        showToast?.('Error exporting Draft Detailed Description');
      }
    };

    const renderParagraphs = () =>
      detailedDescriptionText.split('\n\n').filter((p: string) => p.trim()).map((para: string, idx: number) => {
        const trimmed = para.trim();
        const isHeading = trimmed === trimmed.toUpperCase() && trimmed.length < 80 && /[A-Z]/.test(trimmed);
        if (isHeading) {
          return <p key={idx} className={`font-semibold mt-4 mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{trimmed}</p>;
        }
        return <p key={idx} className={`leading-relaxed mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{trimmed}</p>;
      });

    return (
      <div className="space-y-4">
        {showHeader && (
          <div className={`flex items-center justify-between p-4 rounded-lg ${isDarkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-50'}`}>
            <div className="flex items-center gap-3">
              <span className="text-2xl">📋</span>
              <div>
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Draft Detailed Description</h2>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Full paragraph format</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={exportParagraphsToDocx} className={`text-sm px-3 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-green-700 hover:bg-green-600 text-green-100' : 'bg-green-500 hover:bg-green-600 text-white'}`}>
                <Download className="w-4 h-4" />Export DOCX
              </button>
              <button onClick={() => copyToClipboard(detailedDescriptionText)} className={`text-sm px-3 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}>
                <Copy className="w-4 h-4" />Copy All
              </button>
            </div>
          </div>
        )}
        <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
          {detailedDescriptionText ? renderParagraphs() : (
            <p className={`text-sm italic ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No detailed description text available.</p>
          )}
        </div>
      </div>
    );
  }
  // ---- End paragraph mode render ----

  const toggleSection = (index) => {
    setExpandedSections(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Helper function to highlight search terms
  const highlightText = (text, searchTerm) => {
    if (!searchTerm) return text;
    
    let regex;
    if (matchesWildcard) {
      // Convert wildcard pattern to regex for highlighting
      const escapedPattern = searchTerm
        .toLowerCase()
        .split('*')
        .map(part => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .join('.*?');
      regex = new RegExp(`(${escapedPattern})`, 'gi');
    } else {
      const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      regex = new RegExp(`(${escapedSearchTerm})`, 'gi');
    }
    
    const parts = text.split(regex);
    return parts.map((part, index) => {
      const isMatch = index % 2 === 1; // Odd indices are matches due to split with capturing group
      return isMatch ? (
        <mark key={index} className={`${isDarkMode ? 'bg-yellow-600 text-yellow-100' : 'bg-yellow-200 text-yellow-900'} px-1 rounded`}>
          {part}
        </mark>
      ) : part;
    });
  };

  // Filter sections and paragraphs based on search term
  const filteredSections = detailedDescription.sections?.map(section => {
    if (!searchTerm) return section;
    
    // Check if section heading matches
    const headingMatches = matchesWildcard 
      ? matchesWildcard(section.heading, searchTerm)
      : section.heading.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filter paragraphs to only show those with matches
    const matchingParagraphs = section.paragraphs?.filter(paragraph => {
      if (matchesWildcard) return matchesWildcard(paragraph, searchTerm);
      return paragraph.toLowerCase().includes(searchTerm.toLowerCase());
    }) || [];
    
    // Include section if heading matches OR it has matching paragraphs
    if (headingMatches || matchingParagraphs.length > 0) {
      return {
        ...section,
        paragraphs: matchingParagraphs.length > 0 ? matchingParagraphs : section.paragraphs,
        headingMatches,
        hasMatchingParagraphs: matchingParagraphs.length > 0
      };
    }
    
    return null;
  }).filter(Boolean) || [];

  const renderSection = (section, index) => {
    const isExpanded = expandedSections[index] !== false; // Default to expanded
    
    return (
      <div key={index} className={`mb-6 border rounded-lg ${isDarkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'}`}>
        {/* Section Header */}
        <div 
          className={`flex items-center justify-between p-4 cursor-pointer ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
          onClick={() => toggleSection(index)}
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">📝</span>
            <div>
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {searchTerm ? highlightText(section.heading, searchTerm) : section.heading}
              </h3>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {section.paragraphs?.length || 0} paragraph{(section.paragraphs?.length || 0) !== 1 ? 's' : ''}
                {searchTerm && section.hasMatchingParagraphs && (
                  <span className={`ml-2 px-2 py-1 text-xs rounded ${isDarkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'}`}>
                    matching
                  </span>
                )}
                {searchTerm && section.headingMatches && !section.hasMatchingParagraphs && (
                  <span className={`ml-2 px-2 py-1 text-xs rounded ${isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                    title match
                  </span>
                )}
              </p>
            </div>
          </div>
          <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {isExpanded ? '▲' : '▼'}
          </span>
        </div>

        {/* Section Content */}
        {isExpanded && (
          <div className={`px-4 pb-4 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
            {section.paragraphs && section.paragraphs.map((paragraph, paragraphIndex) => (
              <div key={paragraphIndex} className="mb-4 last:mb-0">
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-start justify-between mb-2">
                    <span className={`text-xs font-medium px-2 py-1 rounded ${isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                      Paragraph {paragraphIndex + 1}
                    </span>
                    <button
                      onClick={() => copyToClipboard(paragraph)}
                      className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                    >
                      <Copy className="w-3 h-3" />
                      Copy Paragraph
                    </button>
                  </div>
                  <div className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                    {searchTerm ? highlightText(paragraph, searchTerm) : paragraph}
                  </div>
                </div>
              </div>
            ))}

            {/* Section Actions */}
            <div className="flex gap-2 mt-4 pt-3 border-t border-gray-300 dark:border-gray-600">
              <button
                onClick={() => copyToClipboard(section.paragraphs?.join('\n\n') || '')}
                className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                <Copy className="w-3 h-3" />
                Copy Section
              </button>
              {addToResources && (
                <button
                  onClick={() => {
                    const sectionText = `# ${section.heading}\n\n${section.paragraphs?.join('\n\n') || ''}`;
                    addToResources({
                      type: 'detailed_description_section',
                      title: section.heading,
                      content: sectionText,
                      source: 'Draft Detailed Description Task'
                    });
                    showToast?.('Section added to resources!');
                  }}
                  className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-green-700 hover:bg-green-600 text-green-100' : 'bg-green-100 hover:bg-green-200 text-green-800'}`}
                >
                  Add to Resources
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const exportToDocx = async () => {
    try {
      const children: Paragraph[] = [];

      // Title
      children.push(
        new Paragraph({
          text: "Draft Detailed Description",
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        })
      );

      // Add summary if available
      if (detailedDescription.summary) {
        children.push(
          new Paragraph({
            text: "Summary",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 300, after: 200 },
          }),
          new Paragraph({
            text: detailedDescription.summary,
            spacing: { after: 300, line: 360 },
          })
        );
      }

      // Generate sections
      detailedDescription.sections.forEach((section: any) => {
        // Section heading
        children.push(
          new Paragraph({
            text: section.heading,
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 300, after: 200 },
          })
        );

        // Section paragraphs
        if (section.paragraphs && section.paragraphs.length > 0) {
          section.paragraphs.forEach((paragraph: string) => {
            children.push(
              new Paragraph({
                text: paragraph,
                spacing: { after: 200, line: 360 },
              })
            );
          });
        }
      });

      // Create Word document
      const doc = new Document({
        sections: [{
          properties: {},
          children: children,
        }],
      });

      // Generate and download the document
      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'draft-detailed-description.docx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      if (showToast) {
        showToast('Draft Detailed Description exported to Word document');
      }
    } catch (error) {
      console.error('Error exporting Draft Detailed Description to DOCX:', error);
      if (showToast) {
        showToast('Error exporting Draft Detailed Description');
      }
    }
  };

  const allSectionsText = detailedDescription.sections
    .map(section => `# ${section.heading}\n\n${section.paragraphs?.join('\n\n') || ''}`)
    .join('\n\n---\n\n');

  return (
    <div className="space-y-4">
      {showHeader && (
        <>
          {/* Header */}
          <div className={`flex items-center justify-between p-4 rounded-lg ${isDarkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-50'}`}>
            <div className="flex items-center gap-3">
              <span className="text-2xl">📋</span>
              <div>
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Draft Detailed Description
                </h2>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {searchTerm
                    ? `${filteredSections.length} of ${detailedDescription.sections?.length || 0} sections shown`
                    : `${detailedDescription.sections?.length || 0} section${(detailedDescription.sections?.length || 0) !== 1 ? 's' : ''} generated`
                  }
                </p>
              </div>
            </div>

            {/* Global Actions */}
            <div className="flex gap-2">
          <button
            onClick={exportToDocx}
            className={`text-sm px-3 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-green-700 hover:bg-green-600 text-green-100' : 'bg-green-500 hover:bg-green-600 text-white'}`}
            title="Export detailed description as Word document"
          >
            <Download className="w-4 h-4" />
            Export DOCX
          </button>
          <button
            onClick={() => copyToClipboard(allSectionsText)}
            className={`text-sm px-3 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
          >
            <Copy className="w-4 h-4" />
            Copy All
          </button>
          {onAddToInput && (
            <button
              onClick={() => {
                onAddToInput(allSectionsText);
                showToast?.('Draft detailed description added to chat input!');
              }}
              className={`text-sm px-3 py-1 rounded ${isDarkMode ? 'bg-blue-700 hover:bg-blue-600 text-blue-100' : 'bg-blue-100 hover:bg-blue-200 text-blue-800'}`}
            >
              Add to Chat
            </button>
          )}
          {onCopyResult && (
            <button
              onClick={() => {
                onCopyResult();
                showToast?.('Draft detailed description copied to clipboard!');
              }}
              className={`text-sm px-3 py-1 rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-100' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
            >
              Copy Result
            </button>
          )}
        </div>
      </div>

          {/* Expand/Collapse All Controls */}
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => {
                const allExpanded = {};
                (searchTerm ? filteredSections : detailedDescription.sections).forEach((_, index) => {
                  allExpanded[index] = true;
                });
                setExpandedSections(allExpanded);
              }}
              className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
            >
              Expand All
            </button>
            <button
              onClick={() => {
                const allCollapsed = {};
                (searchTerm ? filteredSections : detailedDescription.sections).forEach((_, index) => {
                  allCollapsed[index] = false;
                });
                setExpandedSections(allCollapsed);
              }}
              className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
            >
              Collapse All
            </button>
          </div>
        </>
      )}

      {/* Sections */}
      <div className="space-y-4">
        {(searchTerm ? filteredSections : detailedDescription.sections).map((section, index) => renderSection(section, index))}

        {/* No results message */}
        {searchTerm && filteredSections.length === 0 && (
          <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            No sections match your search criteria.
          </div>
        )}
      </div>
    </div>
  );
};

export default DraftDetailedDescriptionDisplay;